import { Add, Edit, Remove } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { EditLegalClientDialog } from '../../../Clients/EditLegalClientDialog';
import { useAlert } from '../../../context/AlertContext';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { useGetRelationshipRoles } from '../../../hooks/data';
import { useGetRelationshipTypeHasRelationshipRole } from '../../../hooks/reactQuerys/RelationshipQueries';
import { ILegalClient } from '../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionRelationshipMember } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { AssignLegalClientsDialog } from '../../LegalClient/AssignLegalClientsDialog';
import { RelationshipMemberDataEditDialog } from './RelationshipMemberDataEditDialog';

interface IProps {
  title: string;
  relationship: ILegalTransactionRelationship;
  subtitle?: string;
  limit?: number;
  members: ILegalTransactionRelationshipMember[];
  updateMember: (member: ILegalTransactionRelationshipMember) => void;
  assignedLegalClients: ILegalClient[];
  setAssignedLegalClients: (newLegalClients: ILegalClient[]) => void;
}

export const RelationshipMembers: React.FC<IProps> = ({
  title,
  relationship,
  subtitle,
  limit,
  members,
  updateMember,
  assignedLegalClients,
  setAssignedLegalClients,
}) => {
  const [isMountAssignClients, setIsMountAssignClients] = useState(false);
  const [editedMemberRelationshipDataId, setEditedMemberRelationshipDataId] =
    useState<number>();
  const [editedLegalClientId, setEditedLegalClientId] = useState<number>();
  const { showAlert } = useAlert();
  const { relationshipRoles, isLoadingRelationshipRoles } =
    useGetRelationshipRoles();
  const {
    relationshipTypeHasRelationshipRole,
    isLoadingRelationshipTypeHasRelationshipRole,
  } = useGetRelationshipTypeHasRelationshipRole();

  const isReachedLimit = useMemo(
    () => !!(limit && members.length === limit),
    [limit, members.length]
  );

  const assignedIds = useMemo(
    () => assignedLegalClients.map(x => x.idLegalClient),
    [assignedLegalClients, setAssignedLegalClients]
  );

  if (
    isLoadingRelationshipRoles ||
    isLoadingRelationshipTypeHasRelationshipRole
  ) {
    return <LoadingSpinner />;
  }

  if (!relationshipRoles || !relationshipTypeHasRelationshipRole) {
    return <Alert severity='error'>Fehler beim Laden der Daten</Alert>;
  }

  return (
    <>
      <Box sx={{ mt: 5 }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant='h5'>{title}</Typography>
          {subtitle && <Typography>{subtitle}</Typography>}
        </Box>
        <Button
          disabled={isReachedLimit}
          sx={{ float: 'right' }}
          variant='contained'
          onClick={() => setIsMountAssignClients(true)}
        >
          Hinzufügen
        </Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nr.</TableCell>
            <TableCell>Vorname</TableCell>
            <TableCell>Nachname</TableCell>
            <TableCell>Rolle aus Dialog</TableCell>
            <TableCell>Aktionen</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {members.map((m, i) => (
            <TableRow key={i}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{m.LegalClient.FirstName}</TableCell>
              <TableCell>{m.LegalClient.LastName}</TableCell>
              <TableCell>
                {m.ExternalRole ? (
                  <Tooltip title='Originalrolle aus dem ki-dia-Dialog'>
                    <Chip
                      size='small'
                      variant='outlined'
                      label={m.ExternalRole}
                    />
                  </Tooltip>
                ) : (
                  <Typography variant='caption' color='text.secondary'>
                    –
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {assignedIds.includes(m.idLegalClient) ? (
                  <Tooltip title='Aus diesem Vorgang entfernen — der Mandant bleibt im System gespeichert und kann wieder hinzugefügt werden'>
                    <IconButton
                      onClick={() => {
                        setAssignedLegalClients(
                          assignedLegalClients.filter(
                            x => x.idLegalClient !== m.idLegalClient
                          )
                        );
                      }}
                    >
                      <Remove />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title='Als Vorgangsbeteiligten zu diesem Vorgang hinzufügen'>
                    <IconButton
                      onClick={() =>
                        setAssignedLegalClients([
                          ...assignedLegalClients,
                          m.LegalClient,
                        ])
                      }
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title='Mandantendaten bearbeiten'>
                  <IconButton
                    onClick={() => setEditedLegalClientId(m.idLegalClient)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                {m.data?.type && (
                  <Button
                    variant='outlined'
                    onClick={() => {
                      setEditedMemberRelationshipDataId(m.idLegalClient);
                    }}
                  >
                    Datenerfassung bearbeiten
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isMountAssignClients && (
        <AssignLegalClientsDialog
          limit={limit}
          assignedLegalClients={assignedLegalClients}
          setAssignedLegalClients={setAssignedLegalClients}
          unmount={() => setIsMountAssignClients(false)}
        />
      )}
      {editedLegalClientId && (
        <EditLegalClientDialog
          saveMethod='saveAndClose'
          editedLegalClientId={editedLegalClientId}
          unmount={editedLegalClient => {
            const editedMember = members.find(
              x => x.idLegalClient === editedLegalClient?.idLegalClient
            );

            if (editedLegalClient && editedMember) {
              updateMember({
                ...editedMember,
                LegalClient: editedLegalClient,
              });

              setAssignedLegalClients(
                assignedLegalClients.map(x =>
                  x.idLegalClient === editedLegalClient.idLegalClient
                    ? editedLegalClient
                    : x
                )
              );
            }

            setEditedLegalClientId(undefined);
          }}
        />
      )}
      {editedMemberRelationshipDataId && (
        <RelationshipMemberDataEditDialog
          relationship={relationship}
          members={members}
          memberLegalClientId={editedMemberRelationshipDataId}
          onClose={() => {
            setEditedMemberRelationshipDataId(undefined);
          }}
          onSave={(idLegalClient, data) => {
            console.log('onSave', idLegalClient, data);
            const existingMember = members.find(
              x => x.idLegalClient === idLegalClient
            );

            if (!existingMember) {
              showAlert({
                text: 'Fehler: Mitglied nicht gefunden',
                severity: 'error',
              });
              return;
            }

            updateMember({
              ...existingMember,
              data,
            });

            showAlert({ text: 'Datenerfassung erfolgreich bearbeitet' });
          }}
        />
      )}
    </>
  );
};
